<template>
  <vxe-grid
    max-height="500"
    :data="table.data"
    :columns="table.columns"
    :cell-style="cellStyle"
    :loading="tableLoading" />
</template>
<script>
import * as R from 'ramda'
import { numerify } from '@/filters'
import { uuid } from '@/utils/utils'

const flattenData = (data, depth = 1, billType = 'stat_value') => {
  const result = []

  function traverse (node, currentNames, currentDepth) {
    if (R.is(Array, node.data)) {
      if (currentDepth === depth) {
        // 到达目标深度，处理数据项
        const entry = {}
        for (let i = 1; i <= currentDepth; i++) {
          entry[`name${i}`] = currentNames[i - 1]
        }
        let total = 0
        node.data.forEach(item => {
          entry[item.date_info] = item[billType]
          total += (item[billType] || 0)
        })
        entry.all = total
        result.push(entry)
      } else {
        // 继续深入下一层
        node.data.forEach(child => {
          traverse(child, [...currentNames, child.name], currentDepth + 1)
        })
      }
    }
  }

  data.forEach(item => {
    traverse(item, [item.name], 1)
  })

  return result
}

export default {
  name: 'CostAlarmTable',
  props: {
    form: {
      type: Object,
      default: () => {},
    },
    queryParams: {
      type: Object,
      default: () => {},
    },
    groupList: {
      type: Array,
      default: () => [],
    },
    currencySign: {
      type: String,
      default: () => '',
    },
  },
  data () {
    return {
      originData: [],
      lastOriginData: [],
    }
  },
  computed: {
    range () {
      const { time_range = '' } = this.form
      const range = time_range.toLowerCase()
      return range
    },
    table () {
      const columns = []
      this.groupList.forEach((item, index) => {
        columns.push({
          field: `name${index + 1}`,
          title: item.label,
          width: 200,
          resizable: true,
        })
      })
      const data = flattenData(this.originData, this.groupList.length)
      if (data[0]) {
        const keys = Object.keys(data[0]).filter(key => !columns.some(item => item.field === key))
        keys.map((date, index) => {
          if (this.range === 'dod' || this.range === 'mom') {
            if (index === 0) {
              return
            }
          }
          if (!columns.some(item => item.field === date) && date !== 'all') {
            columns.push({
              field: date,
              title: date,
              minWidth: 200,
              resizable: true,
              formatter: ({ row }) => {
                return row[date] ? `${this.currencySign} ${numerify(row[date], '0,0.00')}` : `${this.currencySign} 0.00`
              },
            })
          }
        })
      }
      return { columns, data }
    },
    lastTableData () {
      return flattenData(this.lastOriginData, this.groupList.length)
    },
  },
  watch: {
    'form.time_range': {
      handler () {
        this.fetchData()
      },
    },
  },
  created () {
    this.analysisM = new this.$Manager('bill_analysises', 'v1')
    this.fetchData()
  },
  methods: {
    async fetchData () {
      this.tableLoading = true
      const list = []
      const queryParams = {
        ...this.queryParams,
      }
      delete queryParams.groups
      let data_type = 'day'
      let start_date = this.$moment().subtract(7, 'day').format('YYYY-MM-DD') + 'TZ'
      let end_date = this.$moment().subtract(1, 'day').format('YYYY-MM-DD') + 'TZ'
      let start_date2 = this.$moment().subtract(1, 'month').subtract(7, 'day').format('YYYY-MM-DD') + 'TZ'
      let end_date2 = this.$moment().subtract(1, 'month').subtract(1, 'day').format('YYYY-MM-DD') + 'TZ'
      if (this.range === 'dod') {
        start_date = this.$moment().subtract(8, 'day').format('YYYY-MM-DD') + 'TZ'
        end_date = this.$moment().subtract(1, 'day').format('YYYY-MM-DD') + 'TZ'
        start_date2 = ''
        end_date2 = ''
      }
      if (this.range === 'mom pc') {
        data_type = 'month'
        start_date = this.$moment().subtract(12, 'month').startOf('month').format('YYYY-MM-DD') + 'TZ'
        end_date = this.$moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD') + 'TZ'
        start_date2 = this.$moment().subtract(24, 'month').startOf('month').format('YYYY-MM-DD') + 'TZ'
        end_date2 = this.$moment().subtract(13, 'month').endOf('month').format('YYYY-MM-DD') + 'TZ'
      }
      if (this.range === 'mom') {
        data_type = 'month'
        start_date = this.$moment().subtract(13, 'month').startOf('month').format('YYYY-MM-DD') + 'TZ'
        end_date = this.$moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD') + 'TZ'
        start_date2 = ''
        end_date2 = ''
      }
      list.push(this.analysisM.list({
        params: {
          ...queryParams,
          $t: uuid(),
          data_type,
          start_date,
          end_date,
        },
      }))
      if (start_date2 && end_date2) {
        list.push(this.analysisM.list({
          params: {
            ...queryParams,
            $t: uuid(),
            data_type,
            start_date: start_date2,
            end_date: end_date2,
          },
        }))
      }
      const res = await Promise.all(list)
      if (res.length === 2) {
        this.originData = res[0].data.data
        this.lastOriginData = res[1].data.data
      }
      if (res.length === 1) {
        this.originData = res[0].data.data
      }
      this.tableLoading = false
    },
    cellStyle ({ row, rowIndex, column }) {
      if (!column.property.startsWith('name')) {
        return this.checkThreshold(row, column) ? { color: 'red' } : {}
      }
      return {}
    },
    checkThreshold (row, column) {
      const currentValue = row[column.property]
      let lastValue = 0
      const nameKeys = Object.keys(row).filter(key => key.startsWith('name'))
      if (this.range === 'dod pc') {
        const property = this.$moment(column.property).subtract(1, 'month').format('YYYY-MM-DD')
        lastValue = this.findLastValue(row, nameKeys, property)
      }
      if (this.range === 'dod') {
        const property = this.$moment(column.property).subtract(1, 'day').format('YYYY-MM-DD')
        lastValue = row[property]
      }
      if (this.range === 'mom pc') {
        const property = this.$moment(column.property).subtract(1, 'month').format('YYYY-MM')
        lastValue = this.findLastValue(row, nameKeys, property)
      }
      if (this.range === 'mom') {
        const property = this.$moment(column.property).subtract(1, 'month').format('YYYY-MM')
        lastValue = row[property]
      }
      return this.getThresholdCheck(currentValue, lastValue)
    },
    findLastValue (row, nameKeys, property) {
      const targets = this.lastTableData.filter(item => nameKeys.every(key => row[key] === item[key]))
      if (targets.length === 0) {
        return 0
      }
      return targets[0][property] || 0
    },
    getThresholdCheck (currentValue = 0, lastValue = 0) {
      const { trend, threshold_type, threshold } = this.form
      if (!threshold) {
        return false
      }
      if (currentValue === 0 && lastValue === 0) {
        return false
      }
      let check = true
      if (trend === 'ASC' && currentValue < lastValue) {
        check = false
      }
      if (trend === 'DESC' && currentValue > lastValue) {
        check = false
      }
      if (trend === 'ALL' && currentValue === lastValue) {
        check = false
      }
      if (threshold_type === 'absolute') {
        if (trend === 'ASC' && currentValue - lastValue < threshold) {
          check = false
        }
        if (trend === 'DESC' && lastValue - currentValue < threshold) {
          check = false
        }
        if (trend === 'ALL' && Math.abs(currentValue - lastValue) < threshold) {
          check = false
        }
      }
      if (threshold_type === 'relative') {
        if (trend === 'ASC' && (currentValue - lastValue) / lastValue * 100 < threshold) {
          check = false
        }
        if (trend === 'DESC' && (lastValue - currentValue) / currentValue * 100 < threshold) {
          check = false
        }
        if (trend === 'ALL' && Math.abs((lastValue - currentValue) / currentValue * 100) < threshold) {
          check = false
        }
      }
      return check
    },
  },
}
</script>
